/**
 * ============================================================================
 * [P3-02-B] useTrackingData Hook
 *
 * 트래킹 데이터 계산 및 관리
 *
 * 기능:
 * - texts 배열로부터 메트릭 계산
 * - 차트 데이터 생성
 * - 날짜 범위 필터링
 * ============================================================================
 */

import { useMemo, useState, useCallback } from 'react'
import { useTexts } from './useTexts'
import type { SavedText } from '@/types'

// ============================================================================
// [P3-02-B] 타입 정의
// ============================================================================
export interface TrackingMetrics {
  totalTexts: number
  totalCharacters: number
  averageChars: number
  textsThisWeek: number
  textsThisMonth: number
  streak: number
  longestStreak: number
  mostProductiveDay: string
  mostProductiveHour: number
}

export interface ChartData {
  daily: { labels: string[]; values: number[] }
  weekday: { labels: string[]; values: number[] }
  hourly: { labels: string[]; values: number[] }
}

export interface DateRange {
  start: Date
  end: Date
}

export interface UseTrackingDataReturn {
  metrics: TrackingMetrics
  chartData: ChartData
  isLoading: boolean
  dateRange: DateRange
  setDateRange: (range: DateRange) => void
}

// ============================================================================
// [P3-02-B] Helper Functions
// ============================================================================
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']

/**
 * 연속 작성일(streak) 계산
 */
function calculateStreak(texts: SavedText[]): { current: number; longest: number } {
  if (texts.length === 0) return { current: 0, longest: 0 }

  // 날짜별 그룹화
  const dateSet = new Set(
    texts.map((t) => new Date(t.createdAt).toDateString())
  )

  // 연속 일수 계산
  let current = 0
  let longest = 0
  let tempStreak = 0
  const today = new Date()

  for (let i = 0; i < 365; i++) {
    const date = new Date(today)
    date.setDate(date.getDate() - i)
    const dateStr = date.toDateString()

    if (dateSet.has(dateStr)) {
      tempStreak++
      if (i < 30) current = tempStreak // 최근 30일 내에서만 현재 streak
      longest = Math.max(longest, tempStreak)
    } else {
      tempStreak = 0
    }
  }

  return { current, longest }
}

/**
 * 가장 생산적인 요일 계산
 */
function getMostProductiveDay(texts: SavedText[]): string {
  if (texts.length === 0) return WEEKDAYS[0]

  const counts = new Array(7).fill(0)
  texts.forEach((t) => {
    const day = new Date(t.createdAt).getDay()
    counts[day]++
  })
  const maxIndex = counts.indexOf(Math.max(...counts))
  return WEEKDAYS[maxIndex]
}

/**
 * 가장 생산적인 시간대 계산
 */
function getMostProductiveHour(texts: SavedText[]): number {
  if (texts.length === 0) return 0

  const counts = new Array(24).fill(0)
  texts.forEach((t) => {
    const hour = new Date(t.createdAt).getHours()
    counts[hour]++
  })
  return counts.indexOf(Math.max(...counts))
}

// ============================================================================
// [P3-02-B] useTrackingData Hook 구현
// ============================================================================
export function useTrackingData(): UseTrackingDataReturn {
  const { texts, isLoading } = useTexts()

  // ========================================================================
  // 날짜 범위 상태 (기본: 최근 1개월)
  // ========================================================================
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const end = new Date()
    const start = new Date()
    start.setMonth(start.getMonth() - 1)
    return { start, end }
  })

  // ========================================================================
  // 날짜 범위로 필터링된 texts
  // ========================================================================
  const filteredTexts = useMemo(() => {
    return texts.filter((t) => {
      const date = new Date(t.createdAt)
      return date >= dateRange.start && date <= dateRange.end
    })
  }, [texts, dateRange])

  // ========================================================================
  // 메트릭 계산
  // ========================================================================
  const metrics = useMemo<TrackingMetrics>(() => {
    const now = new Date()
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

    // charCount가 있으면 사용, 없으면 content.length로 계산
    const totalChars = filteredTexts.reduce(
      (sum, t) => sum + (t.charCount || t.content.length),
      0
    )
    const { current, longest } = calculateStreak(filteredTexts)

    return {
      totalTexts: filteredTexts.length,
      totalCharacters: totalChars,
      averageChars: filteredTexts.length > 0
        ? Math.round(totalChars / filteredTexts.length)
        : 0,
      textsThisWeek: filteredTexts.filter(
        (t) => new Date(t.createdAt) >= weekAgo
      ).length,
      textsThisMonth: filteredTexts.filter(
        (t) => new Date(t.createdAt) >= monthAgo
      ).length,
      streak: current,
      longestStreak: longest,
      mostProductiveDay: getMostProductiveDay(filteredTexts),
      mostProductiveHour: getMostProductiveHour(filteredTexts),
    }
  }, [filteredTexts])

  // ========================================================================
  // 차트 데이터 생성
  // ========================================================================
  const chartData = useMemo<ChartData>(() => {
    // 일별 데이터 (최근 30일)
    const dailyLabels: string[] = []
    const dailyValues: number[] = []
    for (let i = 29; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      const dateStr = date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
      dailyLabels.push(dateStr)

      const count = filteredTexts.filter(
        (t) => new Date(t.createdAt).toDateString() === date.toDateString()
      ).length
      dailyValues.push(count)
    }

    // 요일별 데이터
    const weekdayCounts = new Array(7).fill(0)
    filteredTexts.forEach((t) => {
      weekdayCounts[new Date(t.createdAt).getDay()]++
    })

    // 시간대별 데이터
    const hourlyCounts = new Array(24).fill(0)
    filteredTexts.forEach((t) => {
      hourlyCounts[new Date(t.createdAt).getHours()]++
    })

    return {
      daily: { labels: dailyLabels, values: dailyValues },
      weekday: { labels: WEEKDAYS, values: weekdayCounts },
      hourly: {
        labels: Array.from({ length: 24 }, (_, i) => `${i}시`),
        values: hourlyCounts,
      },
    }
  }, [filteredTexts])

  // ========================================================================
  // setDateRange callback (안정적인 참조)
  // ========================================================================
  const handleSetDateRange = useCallback((range: DateRange) => {
    setDateRange(range)
  }, [])

  // ========================================================================
  // Return
  // ========================================================================
  return {
    metrics,
    chartData,
    isLoading,
    dateRange,
    setDateRange: handleSetDateRange,
  }
}

export default useTrackingData
